'use client';

import Link from 'next/link';
import { useLocalHref } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { TrendVideoMedia } from './trend-video-media';
import { cn } from '../utils/cn';

export interface TrendsScreenItem {
  id: string;
  name: string;
  category: string | null;
  examples: Array<{
    imageUrl: string;
    thumbnailUrl?: string | null;
    isVideo: boolean;
  }>;
}

interface TrendsScreenProps {
  title: string;
  category: string | null;
  categories?: string[];
  trends?: TrendsScreenItem[];
  isLoading?: boolean;
  onCategoryChange: (category: string | null) => void;
  onTrendClick?: () => void;
  /** Optional back-button handler for the title row. Web mobile passes
   * this so a category view shows a ← that returns to "Все тренды";
   * the miniapp leaves it undefined and relies on Telegram's native
   * BackButton instead. */
  onBack?: () => void;
}

export function TrendsScreen({
  title,
  category,
  categories,
  trends,
  isLoading = false,
  onCategoryChange,
  onTrendClick,
  onBack,
}: TrendsScreenProps) {
  const trendsBase = useLocalHref('/trends');
  return (
    <div className="flex min-h-0 flex-col">
      <div className="relative z-10 flex items-center gap-3 px-4 pb-3 pt-1">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Назад"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#242424]/40"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#fafafa"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4L5 10L11 16" />
              <line x1="5" y1="10" x2="16" y2="10" />
            </svg>
          </button>
        )}
        <h1 className="text-lg font-benzin font-semibold text-white">
          {title}
        </h1>
      </div>

      <div className="relative z-10 flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-3">
        {categories ? (
          <>
            <button
              type="button"
              onClick={() => onCategoryChange(null)}
              className={cn(
                'flex-shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors',
                !category ? 'bg-white/95 text-black' : 'bg-black/15 text-white',
              )}
            >
              Все
            </button>
            {categories.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => onCategoryChange(item)}
                className={cn(
                  'flex-shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors',
                  category === item ? 'bg-white/95 text-black' : 'bg-black/15 text-white',
                )}
              >
                {item}
              </button>
            ))}
          </>
        ) : (
          <>
            {[1, 2, 3, 4, 5].map((item) => (
              <div
                key={item}
                className="h-8 flex-shrink-0 animate-pulse rounded-full bg-black/15"
                style={{ width: `${50 + item * 10}px` }}
              />
            ))}
          </>
        )}
      </div>

      <div className="relative z-10 flex-1 px-4 pb-4">
        {isLoading ? (
          <TrendsScreenSkeleton />
        ) : trends && trends.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {trends.map((trend) => (
              <Link
                key={trend.id}
                href={`${trendsBase}/${trend.id}`}
                onClick={onTrendClick}
                className="group"
              >
                <div
                  className="relative overflow-hidden rounded-[20px_4px_20px_4px] bg-card"
                  style={{ aspectRatio: '172/258' }}
                >
                  {trend.examples[0] && (
                    trend.examples[0].isVideo ? (
                      <TrendVideoMedia
                        src={trend.examples[0].imageUrl}
                        poster={trend.examples[0].thumbnailUrl}
                        alt={trend.name}
                      />
                    ) : (
                      <S3Image
                        src={trend.examples[0].imageUrl}
                        alt={trend.name}
                        fill
                        className="object-cover"
                        showSkeleton={false}
                        fadeDuration={180}
                      />
                    )
                  )}
                  <div className="absolute bottom-0 left-0 h-[115px] w-full bg-gradient-to-t from-black/[0.87] to-transparent opacity-70" />
                  <div className="absolute bottom-2.5 left-2.5 right-2.5 font-benzin text-base font-semibold leading-tight text-white">
                    {trend.name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">Тренды не найдены</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function TrendsScreenSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-2 px-4">
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <div
          key={item}
          className="animate-pulse rounded-[20px_4px_20px_4px] bg-card"
          style={{ aspectRatio: '172/258' }}
        />
      ))}
    </div>
  );
}
